import { ConfirmButton } from '@/components/buttons/components';
import { CheckIcon } from '@/components/icons';
import {
  Box,
  Button,
  CircularProgress,
  OutlinedInput,
  Typography,
} from '@mui/material';
import styled from 'styled-components';

// Reserve space for the widest possible row number ("31." at MAX_LABELS) so
// the input field next to it doesn't shift between 1- and 2-digit rows.
const CHALLENGE_ROW_NUMBER_WIDTH = '24px';

export const ChallengeContainer = styled(Box)`
  width: 100%;
  max-width: 720px;
  margin: 0 auto;
  padding: ${({ theme }) => theme.spacing(6, 2)};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(6)};
`;

export const ChallengeTitle = styled(Typography)`
  font-size: 24px;
  font-weight: 700;
  text-align: center;
`;

export const ChallengeDescription = styled(Typography)`
  color: #5d6081;
  text-align: center;
  font-size: 16px;
  font-weight: 500;
  white-space: pre-line;
`;

export const ChallengeSection = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
`;

export const ChallengeSectionLabel = styled(Typography)`
  flex: 1;
  font-weight: 500;
  font-size: 20px;
`;

export const ChallengeField = styled(OutlinedInput)`
  background-color: ${({ theme }) => theme.palette.background.default};

  & .MuiOutlinedInput-input {
    background-color: transparent;
  }

  /* Browser autofill paints its own background straight onto the <input>
     (a box-shadow inset is the only way WebKit lets us override it), which
     otherwise leaves a visible seam against the end-adornment icon next to
     it — that icon sits outside the <input> and always keeps this field's
     normal background. */
  & input:-webkit-autofill,
  & input:-webkit-autofill:hover,
  & input:-webkit-autofill:focus {
    -webkit-box-shadow: 0 0 0 1000px
      ${({ theme }) => theme.palette.background.default} inset;
    -webkit-text-fill-color: ${({ theme }) => theme.palette.text.primary};
  }
`;

export const ChallengeEntryField = styled(ChallengeField)`
  width: 220px;
`;

export const ChallengeTotalField = styled(ChallengeEntryField)`
  font-weight: 700;

  & .MuiInputBase-input.Mui-disabled {
    -webkit-text-fill-color: currentColor;
    color: ${({ theme }) => theme.palette.text.primary};
  }
`;

export const ChallengeRow = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(4)};
  padding: ${({ theme }) => theme.spacing(3, 0)};
  border-bottom: 1px solid #eef0f7;
`;

export const ChallengeRowGroup = styled(Box)`
  padding: ${({ theme }) => theme.spacing(3, 0)};
  border-bottom: 1px solid #eef0f7;
`;

export const ChallengeRowContent = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(4)};
`;

export const ChallengeRowNumber = styled(Typography)`
  font-size: 16px;
  font-weight: 500;
  color: ${({ theme }) => theme.palette.text.primary};
  min-width: ${CHALLENGE_ROW_NUMBER_WIDTH};
  flex-shrink: 0;
`;

export const ChallengeRowLabel = styled(Typography)`
  flex: 1;
  font-size: 16px;
  font-weight: 500;
`;

export const ChallengeRowTotalLabel = styled(ChallengeRowLabel)`
  font-weight: 700;
`;

export const ChallengeFieldColumn = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

export const ChallengeFieldError = styled(Typography)`
  color: ${({ theme }) => theme.palette.error.main};
  font-size: 12px;
  margin-top: ${({ theme }) => theme.spacing(1)};
`;

export const ChallengeLabelRowError = styled(ChallengeFieldError)`
  /* Matches the number column's reserved width plus the row's gap
     (theme.spacing(4)), so this lines up under the input field above it
     instead of under the row number. */
  margin-left: calc(
    ${CHALLENGE_ROW_NUMBER_WIDTH} + ${({ theme }) => theme.spacing(4)}
  );
`;

export const ChallengeTotalRow = styled(ChallengeRow)`
  font-weight: 700;
  border-bottom: none;
`;

export const ChallengeAddLabelButton = styled(Button)`
  min-width: 160px;
`;

export const ChallengeLoading = styled(Box)`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 50vh;
`;

export const ChallengeSavedStatusIcon = styled(CheckIcon)`
  width: 14px;
  height: 14px;
`;

export const ChallengeCenteredActionRow = styled(Box)`
  display: flex;
  justify-content: center;
  margin-top: ${({ theme }) => theme.spacing(2)};
`;

export const ChallengeHeaderRow = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(2)};
`;

export const FlexChallengeTitle = styled(ChallengeTitle)`
  flex: 1;
`;

export const SavingSpinner = styled(CircularProgress).attrs({
  size: 16,
  color: 'inherit',
})`
  margin-right: ${({ theme }) => theme.spacing(1)};
`;

export const ChallengeLabelsFooterRow = styled(Box)`
  display: flex;
  align-items: center;
  margin-top: ${({ theme }) => theme.spacing(3)};
  scroll-margin-bottom: ${({ theme }) => theme.spacing(6)};
`;

export const ChallengeSaveButton = styled(ConfirmButton)`
  margin-left: auto;
`;
